import { Typography } from "@mui/material"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import UIIcon from "../../../components/UIIcon"
import { RootState } from "../../../store/store"
import {
  AccordionBody,
  AccordionHeader,
  Amount,
  CenterContainer,
  ContentContainer,
  DetailContainer,
  DetailLabel,
  DetailsContainer,
  DetailValue,
  RecipientContainer,
  SourceDescription,
  StyledAccordion,
  StyledButton,
  StyledDivider,
  TopContainer,
  IconContainer,
  ScrollContainer,
  ButtonsContainer,
  DetailsContainerTextOverflow,
  StatusChecked,
} from "./styled"
import IconBox from "../../../components/IconBox"
import BankIcon from "../../../components/BankIcon"
import { formatAmount, formatPhoneNumber } from "../../../utils/formatters"
import { resetSlice } from "../../../store/slices/c2cSlice"
import { SbpOutcomeTransferStatusProps } from "./props"
import {
  resetSteps,
  setNextStep,
} from "../../../store/slices/routingSlice/routingSlice"
import { sendCustomEvent } from "../../../events/events"
import { useGetSbpOutcomeTransferStatus } from "../../../api/hooks/useGetSbpOutcomeTransferStatus"
import { STEPS_NAME } from "../../../store/slices/routingSlice/flowsSteps"
import SBPIconNoExt from "../../../components/SBPIconNoExt"
import { getTimezone } from "../../../utils/datetime"
import { getStatusDetails } from "../../../utils/operation"
import { RUBLE } from "../../../utils/constants"

const SbpOutcomeTransferStatus = ({
  outcomeTransferId,
  isLoading,
  enablePolling = false,
  setEnablePolling,
}: SbpOutcomeTransferStatusProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const transferForm = useSelector((state: RootState) => state.transferForm)

  const handleQuitEvent = () => {
    sendCustomEvent({
      eventName: "quit",
    })
  }

  const handleDoneButtonClick = () => {
    if (setEnablePolling) setEnablePolling(false)
    handleQuitEvent()
    dispatch(resetSlice())
    dispatch(resetSteps())
    navigate("/quit")
  }
  const handleAgainButtonClick = () => {
    dispatch(setNextStep(STEPS_NAME.c2cPush.createTransfer))
  }

  const { data: transfer, isLoading: isDataLoading } =
    useGetSbpOutcomeTransferStatus(outcomeTransferId, enablePolling)

  if (!transfer) {
    return null
  }

  const statusDetails = getStatusDetails(transfer!.status)

  return (
    <ContentContainer>
      <ScrollContainer>
        <TopContainer>
          <IconBox size="lg" color="yellow">
            <UIIcon iconName={statusDetails.icon} size={80} />
          </IconBox>
          <SourceDescription>{statusDetails.message}</SourceDescription>
          <Amount>
            {formatAmount(transfer.amount)} {RUBLE}
          </Amount>
          {transfer.commissionAmount === 0 && (
            <Typography variant="subtitle1" sx={{ mt: "20px" }}>
              Комиссия не взимается
            </Typography>
          )}
        </TopContainer>
        {!(isDataLoading || isLoading) && (
          <>
            <CenterContainer>
              <IconBox>
                <BankIcon
                  logoUrl={String(transferForm.selectedBank?.logoUrl)}
                  size={46}
                />
              </IconBox>
              <RecipientContainer>
                <Typography variant="h4">
                  {transfer.recipientInfo.pam}
                </Typography>
                <Typography variant="h6">
                  {transferForm.recipient
                    ? formatPhoneNumber(transferForm.recipient?.phone)
                    : ""}
                </Typography>
              </RecipientContainer>
            </CenterContainer>
            <StyledDivider />
            <StyledAccordion>
              <AccordionHeader
                expandIcon={<UIIcon iconName="arrowDown" />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography variant="h4">Подробнее об операции</Typography>
              </AccordionHeader>
              <AccordionBody>
                <DetailsContainer sx={DetailsContainerTextOverflow}>
                  <DetailContainer>
                    <DetailLabel>Статус операции</DetailLabel>
                    <DetailValue>
                      <StatusChecked statusColor={statusDetails.color}>
                        {statusDetails.message}
                      </StatusChecked>
                    </DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>
                      Номер операции в РНКО «Единая Касса»
                    </DetailLabel>
                    <DetailValue>{transfer.outcomeTransferId}</DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>Номер телефона получателя</DetailLabel>
                    <DetailValue>
                      {formatPhoneNumber(transfer.recipientInfo.phoneNumber)}
                    </DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>ФИО получателя</DetailLabel>
                    <DetailValue>{transfer.recipientInfo.pam}</DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>Банк получателя</DetailLabel>
                    <DetailValue>{transfer.bankInfo.title}</DetailValue>
                  </DetailContainer>
                  {transfer.description && (
                    <DetailContainer>
                      <DetailLabel>Сообщение получателю</DetailLabel>
                      <DetailValue>{transfer.description}</DetailValue>
                    </DetailContainer>
                  )}
                  <DetailContainer>
                    <DetailLabel>Сумма перевода</DetailLabel>
                    <DetailValue>
                      {formatAmount(transfer.amount)} {RUBLE}
                    </DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>Комиссия</DetailLabel>
                    <DetailValue>
                      {formatAmount(transfer.commissionAmount)} {RUBLE}
                    </DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>Номер операции в СБП</DetailLabel>
                    <DetailValue>{transfer.operationNumber}</DetailValue>
                  </DetailContainer>
                  <DetailContainer>
                    <DetailLabel>Дата и время операции</DetailLabel>
                    <DetailValue>{getTimezone(transfer.updateOn)}</DetailValue>
                  </DetailContainer>
                </DetailsContainer>
              </AccordionBody>
            </StyledAccordion>
          </>
        )}
        <IconContainer>
          <SBPIconNoExt size={60} />
        </IconContainer>
      </ScrollContainer>
      {statusDetails.isFinal && (
        <ButtonsContainer>
          {statusDetails.isSuccesseful && (
            <StyledButton variant="contained" onClick={handleDoneButtonClick}>
              Готово
            </StyledButton>
          )}
          {!statusDetails.isSuccesseful && (
            <>
              <StyledButton
                variant="contained"
                color="secondary"
                onClick={handleDoneButtonClick}
              >
                Закрыть
              </StyledButton>
              <StyledButton
                variant="contained"
                onClick={handleAgainButtonClick}
              >
                Попробовать еще раз
              </StyledButton>
            </>
          )}
        </ButtonsContainer>
      )}
    </ContentContainer>
  )
}

export default SbpOutcomeTransferStatus
